import ReactECharts from 'echarts-for-react';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * OutreachCostStackChart — blog16, Figure 1: what the do-it-yourself
 * investor-outreach route really costs.
 *
 * Chart-type-first: six cost ranges with very different magnitudes and units →
 * horizontal low–high range bars on a log value axis. A linear axis crushes the
 * per-email verification range into invisibility next to a $25,000 agency
 * retainer; the log axis keeps every range readable. The unit lives in each
 * row's label, never implied by the axis.
 *
 * Data mirrors public/blog/raisy_outreach_infra_costs.csv exactly (Rule 27 —
 * one data file per post; nothing retyped beyond this mirror):
 *   DIY sequencer tool subscription   39–97      USD/month  (vendor pricing 2026)
 *   DIY all-in cold-email stack       80–320     USD/month  (practitioner estimates 2026-08)
 *   Email find + verify per investor  0.04–0.15 USD/investor (Hunter.io 2026 tiers:
 *   find 1 credit + verify 0.5 credit; Starter $49/mo, Growth $149, Business $399)
 *   Sending domain (annual)           10.88–11.08 USD/year  (flat-rate .com registrar 2026-08)
 *   Real mailbox (monthly)            6–7.20     USD/month  (Workspace Business Starter 2026)
 *   Fundraising agency retainer       5000–25000 USD/month  (qubit.capital / waveup 2026; Montfort $1,050–5,000 no success fee)
 *
 * Opaque background; ONE shared formatter for axis, tooltip, and bar labels
 * (Rule 21). Color is never the only identity — every row is labeled.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealLight: '#7FD4DC',
  slate: '#64748B',
};

type CostRow = {
  name: string;
  short: string;
  low: number;
  high: number;
  unit: string;
  color: string;
};

const ROWS: CostRow[] = [
  { name: 'Email find + verify (per investor)', short: 'Find + verify / investor', low: 0.04, high: 0.15, unit: 'USD/investor', color: BRAND.tealLight },
  { name: 'Sending domain (per domain)', short: 'Domain / yr', low: 10.88, high: 11.08, unit: 'USD/year', color: BRAND.tealLight },
  { name: 'Real mailbox (per mailbox)', short: 'Mailbox / mo', low: 6, high: 7.2, unit: 'USD/month', color: BRAND.tealLight },
  { name: 'Sequencer / sending tool', short: 'Sequencer / mo', low: 39, high: 97, unit: 'USD/month', color: BRAND.tealLight },
  { name: 'All-in DIY stack', short: 'All-in DIY / mo', low: 80, high: 320, unit: 'USD/month', color: BRAND.teal },
  { name: 'Fundraising agency retainer', short: 'Agency / mo', low: 5000, high: 25000, unit: 'USD/month', color: BRAND.slate },
];

/** ONE shared number formatter — axis ticks, tooltips, and bar labels all use it. */
const fmt = (v: number) => (v >= 1000 ? v.toLocaleString('en-US') : `${v}`);

const rangeLabel = (r: CostRow) => `$${fmt(r.low)}–${fmt(r.high)}`;

export const OutreachCostStackChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const C = isDark
    ? { bg: '#0F172A', axis: '#E2E8F0', muted: '#94A3B8', grid: '#334155', card: 'bg-slate-900', border: 'border-slate-800', caption: 'text-slate-400' }
    : { bg: '#FFFFFF', axis: '#334155', muted: '#64748B', grid: '#334155', card: 'bg-white', border: 'border-slate-200', caption: 'text-slate-500' };

  // Phone: long row names are the space problem. Shorten the category labels
  // and the gutter together and shrink the range labels so the bars stay the
  // focus at ~343px of usable column width.
  const gutter = narrow ? 118 : 226;
  const chartHeight = height ?? (narrow ? 360 : 380);

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'item',
      appendToBody: true,
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { dataIndex: number }) => {
        const r = ROWS[p.dataIndex];
        return `<b>${r.name}</b><br/>${rangeLabel(r)} <span style="color:${C.muted}">${r.unit}</span>`;
      },
    },
    grid: { left: gutter, right: narrow ? 96 : 130, top: 28, bottom: 44 },
    xAxis: {
      type: 'log',
      min: 0.002,
      max: 30000,
      name: 'cost (log scale — units differ per row, see labels)',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: narrow ? 10 : 11, formatter: (v: number) => `$${fmt(v)}` },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.25 } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: ROWS.map((r) => (narrow ? r.short : r.name)),
      axisLabel: { color: C.axis, fontSize: narrow ? 10 : 11.5 },
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'custom',
        renderItem: (
          _params: unknown,
          api: {
            value: (i: number) => number;
            coord: (v: [number, number]) => [number, number];
          }
        ) => {
          const idx = api.value(2);
          const row = ROWS[idx];
          const pLow = api.coord([api.value(0), idx]);
          const pHigh = api.coord([api.value(1), idx]);
          const barH = narrow ? 12 : 16;
          return {
            type: 'group',
            children: [
              {
                type: 'rect',
                shape: {
                  x: pLow[0],
                  y: pLow[1] - barH / 2,
                  width: Math.max(pHigh[0] - pLow[0], 3),
                  height: barH,
                  r: 3,
                },
                style: { fill: row.color },
              },
              {
                type: 'text',
                style: {
                  x: pHigh[0] + 6,
                  y: pHigh[1],
                  text: rangeLabel(row),
                  fill: C.axis,
                  fontSize: narrow ? 9.5 : 11,
                  fontWeight: 600,
                  align: 'left',
                  verticalAlign: 'middle',
                },
              },
            ],
          };
        },
        data: ROWS.map((r, i) => [r.low, r.high, i]),
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height: chartHeight }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center px-4`}>
        Figure 1. The DIY route, line by line. Bars span the low–high range for each item; the axis is logarithmic
        because a per-email fee and a monthly retainer differ by six orders of magnitude. The two totals — the
        all-in DIY stack and a fundraising agency retainer — are the bottom two rows. Source:
        raisy_outreach_infra_costs.csv.
      </p>
    </div>
  );
};
